import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { AuthLayout } from '../../components/auth/auth-layout.tsx';
import { PasswordInput } from '../../components/auth/password-input.tsx';
import { PasswordStrength } from '../../components/auth/password-strength.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Field } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, ApiError, errorMessage } from '../../lib/api.ts';
import { takeTokenFromHash } from '../../lib/navigation.ts';
import { usePasswordCheck } from '../../lib/use-password-check.ts';

export function ResetPasswordPage() {
  const [token] = useState(takeTokenFromHash);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const lookup = useQuery({
    queryKey: ['password-reset', token],
    queryFn: () =>
      api<{ email: string }>('/auth/password-reset/lookup', { method: 'POST', body: { token } }),
    enabled: token !== null,
    retry: false,
  });
  const serverMessage = usePasswordCheck(
    '/auth/password-reset/check-password',
    { token },
    password,
  );

  const invalid = (
    <AuthLayout title="Enlace no válido">
      <Alert tone="warning">El enlace no es válido, ya se usó o venció. Pide uno nuevo.</Alert>
      <Link to="/olvide-contrasena" className={buttonVariants({ className: 'w-full' })}>
        Pedir un enlace nuevo
      </Link>
    </AuthLayout>
  );
  if (!token) return invalid;
  if (lookup.isPending) return <Loading label="Revisando el enlace…" />;
  if (lookup.error) {
    if (lookup.error instanceof ApiError && lookup.error.status === 404) return invalid;
    return (
      <AuthLayout title="Restablecer contraseña">
        <Alert tone="danger">{errorMessage(lookup.error)}</Alert>
      </AuthLayout>
    );
  }
  const { email } = lookup.data;

  if (done) {
    return (
      <AuthLayout
        title="Contraseña actualizada"
        subtitle="Cerramos tus sesiones abiertas por seguridad."
      >
        <Link
          to={`/iniciar-sesion?correo=${encodeURIComponent(email)}`}
          className={buttonVariants({ className: 'w-full' })}
        >
          Iniciar sesión
        </Link>
      </AuthLayout>
    );
  }

  const submit = async () => {
    setError(null);
    setFieldError(null);
    if (password !== confirmPassword) {
      setFieldError('Las contraseñas no coinciden.');
      return;
    }
    setBusy(true);
    try {
      await api('/auth/password-reset/complete', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'weak_password') setFieldError(err.message);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Elige una contraseña nueva" subtitle={`Para ${email}`}>
      {error && <Alert tone="danger">{error}</Alert>}
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field label="Contraseña nueva" error={fieldError ?? undefined}>
          {(a11y) => (
            <PasswordInput
              {...a11y}
              aria-describedby={[a11y['aria-describedby'], 'password-help']
                .filter(Boolean)
                .join(' ')}
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          )}
        </Field>
        <PasswordStrength
          id="password-help"
          password={password}
          userInputs={[email]}
          serverMessage={serverMessage}
        />
        <Field label="Repite la contraseña">
          {(a11y) => (
            <PasswordInput
              {...a11y}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
              }}
            />
          )}
        </Field>
        <Button
          variant="brand"
          type="submit"
          className="w-full"
          loading={busy}
          disabled={!password}
        >
          Guardar contraseña
        </Button>
      </form>
    </AuthLayout>
  );
}
